import { roleService } from "@/services/api/role.service";
import { roleModel } from "@/models/api/role.model";
import { sharedHelper, alertHelper } from "@/utils";
import i18n from "@/i18n";

export const ROLE_MODULE = "api/role";
export const GET_ALL_ROLE = "getAllRole";
export const CREATE_ROLE = "createRole";
export const GET_ROLE = "getRole";
export const UPDATE_ROLE = "updateRole";
export const UPDATE_ROLE_STATUS = "updateRoleStatus";
export const DELETE_ROLE = "deleteRole";

export const state = {
  list: {
    data: [],
    pagination: {
      pageNumber: 1,
      pageSize: 15,
      lastPage: 1,
      tableRowsCounts: 0,
      total: 0,
    },
  },
  details: {},
  listError: false,
  status: {
    list: true,
    detail: true,
    save: true,
  },
};

function showError(commit, error, type = "save") {
  commit("failure", type);
  alertHelper.alertError({
    message: sharedHelper.getErrorMessage(error),
    title: i18n.t("message.error"),
  });
}

function requireSuccess(result) {
  if (!result?.success)
    throw new Error(result?.message || "Role request failed.");
  return result;
}

async function refresh(dispatch, filter) {
  await dispatch(GET_ALL_ROLE, { filter });
}

export const actions = {
  async [GET_ALL_ROLE]({ commit }, { filter }) {
    commit("requesting");
    try {
      const result = await roleService.getAll(filter);
      requireSuccess(result);
      commit("listSuccess", result);
      return result;
    } catch (error) {
      showError(commit, error, "list");
      return null;
    }
  },
  async [CREATE_ROLE]({ commit, dispatch }, { data, filter }) {
    commit("requesting", "save");
    try {
      const result = await roleService.store(data);
      requireSuccess(result);
      commit("success");
      alertHelper.alertSuccess({
        message: i18n.t("message.createXSuccess", [i18n.t("module.role")]),
      });
      await refresh(dispatch, filter);
      return result;
    } catch (error) {
      showError(commit, error);
      return null;
    }
  },
  async [GET_ROLE]({ commit }, { uuid }) {
    commit("requesting", "detail");
    try {
      const result = await roleService.show(uuid);
      requireSuccess(result);
      commit("detailSuccess", result);
      return result;
    } catch (error) {
      showError(commit, error);
      return null;
    }
  },
  async [UPDATE_ROLE]({ commit, dispatch }, { data, filter }) {
    commit("requesting", "save");
    try {
      const result = await roleService.update(data);
      requireSuccess(result);
      commit("success");
      alertHelper.alertSuccess({
        message: i18n.t("message.updateXSuccess", [i18n.t("module.role")]),
      });
      await refresh(dispatch, filter);
      return result;
    } catch (error) {
      showError(commit, error);
      return null;
    }
  },
  async [UPDATE_ROLE_STATUS]({ commit, dispatch }, { uuid, isActive, filter }) {
    commit("requesting", "save");
    try {
      const result = await roleService.updateStatus(uuid, isActive);
      requireSuccess(result);
      commit("success");
      await refresh(dispatch, filter);
      return result;
    } catch (error) {
      showError(commit, error);
      return null;
    }
  },
  async [DELETE_ROLE]({ commit, dispatch }, { uuid, filter }) {
    commit("requesting", "save");
    try {
      const result = await roleService.softDelete(uuid);
      requireSuccess(result);
      commit("success");
      alertHelper.alertSuccess({
        message: i18n.t("message.deleteXSuccess", [i18n.t("module.role")]),
      });
      await refresh(dispatch, filter);
      return result;
    } catch (error) {
      showError(commit, error);
      return null;
    }
  },
};

export const mutations = {
  requesting(state, type = "list") {
    state.status[type] = false;
    if (type === "list") state.listError = false;
  },
  listSuccess(state, result) {
    const rows = Array.isArray(result?.data)
      ? result.data
      : result?.data?.uuid
      ? [result.data]
      : [];
    state.list.data = rows.map(roleModel);
    state.list.pagination = result?.pagination
      ? sharedHelper.apiPaginationAdapter(result.pagination)
      : {
          pageNumber: 1,
          pageSize: 15,
          lastPage: 1,
          total: rows.length,
          tableRowsCounts: rows.length,
        };
    state.status.list = true;
  },
  detailSuccess(state, result) {
    const payload = result?.data?.role || result?.data || {};
    state.details = roleModel(payload);
    state.status.detail = true;
  },
  success(state) {
    state.status = {
      list: true,
      detail: true,
      save: true,
    };
  },
  failure(state, type) {
    if (type === "list") state.listError = true;
    state.status = {
      list: true,
      detail: true,
      save: true,
    };
  },
};
